import { css, keyframes } from "styled-components";

export const glowTransition = keyframes`
  100% {
    box-shadow: 0 0 0px 3px var(--input-glow-color);
  }
`;

export const glow = (
  type: "border" | "outline" = "border",
  disableShadow = false,
) => css`
  // add transparency to the glow color
  --input-glow-color: color-mix(
    in srgb,
    var(--input-glow, hsl(203, 80%, 45%)),
    transparent 70%
  );
  ${type}: solid 1px var(--input-glow, hsl(203, 80%, 45%));

  // this box shadow is filled in later by the animation
  box-shadow: 0 0 0px 0px var(--input-glow-color);
  ${disableShadow
    ? ""
    : css`
        animation: ${glowTransition} 0.3s 1;
        animation-fill-mode: forwards;
      `}

  // this helps the outline show more consistently (not get cut off by siblings)
  position: relative;
  z-index: 1;
`;

/** Our common input glow. */
export const inputGlow = css`
  &:focus-visible,
  &:focus-within {
    ${glow("outline")}
  }
`;

// basically :focus-within-visible if that existed
export const inputGlowOnChildrenVisible = css`
  &:has(:focus-visible) {
    ${glow("outline")};
  }
`;

export const inputGlowNotOnChildren = css`
  &:focus-visible {
    ${glow("border")}
  }
`;

export const coloredTableStyles = css`
  table.examspark-table,
  /*
    Need an extra selector here to counter the specificity
    of CK's built in table css
  */
  .ck-content .table table.examspark-table {
    --table-color: #666;
    --table-header-color: #eee;
    --table-header-text-color: #000;

    border-collapse: separate;
    border-spacing: 2px;
    border: none;

    &.table-basic {
      border-collapse: collapse;
      border-spacing: 0px;
      --table-color: #333;
    }

    & td,
    & th {
      border: solid 2px var(--table-color);
    }

    & th {
      font-size: 1.1em;
      text-align: left;
      background-color: var(--table-header-color);
      color: var(--table-header-text-color);
    }
    & td,
    & th {
      padding: 0.5rem;
    }
    &.table-red {
      --table-color: #ef476f;
      --table-header-color: #ef476f;
    }
    &.table-yellow {
      --table-color: #ffd166;
      --table-header-color: #ffd166;
    }
    &.table-green {
      --table-color: #06d6a0;
      --table-header-color: #06d6a0;
    }
    &.table-blue {
      --table-color: #118ab2;
      --table-header-color: #118ab2;
    }
    &.table-indigo {
      --table-color: #1b1464;
      --table-header-color: #1b1464;
      --table-header-text-color: #fff;
    }
    &.table-light-red {
      --table-color: #f9acc2;
      --table-header-color: #f9acc2;
    }
    &.table-light-yellow {
      --table-color: #fff1d1;
      --table-header-color: #fff1d1;
    }
    &.table-light-green {
      --table-color: #06d6a0;
      --table-header-color: #06d6a0;
    }
    &.table-light-blue {
      --table-color: #bceaf7;
      --table-header-color: #bceaf7;
    }
    &.table-light-indigo {
      --table-color: #bceaf7;
      --table-header-color: #bceaf7;
    }
    &.table-centered {
      text-align: center;

      & th {
        text-align: center;
      }
    }
  }
`;

export const contentStyles = /* CSS */ `

/** 
 * 1. Repeat all of the default CKE styles for the plugins we use  
 *    The goal is to make it safe to re-copy the styles from the CKE docs
 *    without accidentally undoing our own styles.
 * 
 *    copied from:
 *      https://ckeditor.com/docs/ckeditor5/latest/installation/advanced/content-styles.html
 * 
 *    then s/.ck-content/&/g.
 **/

/* @ckeditor/ckeditor5-table/theme/tablecolumnresize.css */
& .table .ck-table-resized {
  table-layout: fixed;
}
/* @ckeditor/ckeditor5-table/theme/tablecolumnresize.css */
& .table table {
  overflow: hidden;
}
/* @ckeditor/ckeditor5-table/theme/tablecolumnresize.css */
& .table td,
& .table th {
  overflow-wrap: break-word;
  position: relative;
}
/* @ckeditor/ckeditor5-table/theme/table.css */
& .table {
  margin: 0.9em auto;
  display: table;
}
/* @ckeditor/ckeditor5-table/theme/table.css */
& .table table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  height: 100%;
  /* we don't want borders in our rendered content */
  /* border: 1px double hsl(0, 0%, 70%); */
  border-style: double;
  border-width: 1px;
  border-color: transparent;
}
/* @ckeditor/ckeditor5-table/theme/table.css */
& .table table td,
& .table table th {
  min-width: 2em;
  padding: 0.4em;
  /* we don't want borders in our rendered content */
  /* border: 1px solid hsl(0, 0%, 75%); */
  border-style: solid;
  border-width: 1px;
  border-color: transparent;
}
/* @ckeditor/ckeditor5-table/theme/table.css */
& .table table th {
  font-weight: bold;
  background: hsla(0, 0%, 0%, 5%);
}
/* @ckeditor/ckeditor5-table/theme/table.css */
&[dir="rtl"] .table th {
  text-align: right;
}
/* @ckeditor/ckeditor5-table/theme/table.css */
&[dir="ltr"] .table th {
  text-align: left;
}
/* @ckeditor/ckeditor5-table/theme/tablecaption.css */
& .table > figcaption {
  display: table-caption;
  caption-side: top;
  word-break: break-word;
  text-align: center;
  color: var(--ck-color-selector-caption-text);
  background-color: var(--ck-color-selector-caption-background);
  padding: 0.6em;
  font-size: 0.75em;
  outline-offset: -1px;
}

/* @ckeditor/ckeditor5-image/theme/image.css */
& .image {
  display: table;
  clear: both;
  text-align: center;
  margin: 0.9em auto;
  min-width: 50px;
}
/* @ckeditor/ckeditor5-image/theme/image.css */
& .image img {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  min-width: 100%;
  height: auto;
}
/* @ckeditor/ckeditor5-image/theme/image.css */
& .image-inline {
  /*
   * Normally, the .image-inline would have "display: inline-block" and "img { width: 100% }" (to follow the wrapper while resizing).;
   * Unfortunately, together with "srcset", it gets automatically stretched up to the width of the editing root.
   * This strange behavior does not happen with inline-flex.
   */
  display: inline-flex;
  max-width: 100%;
  align-items: flex-start;
}
/* @ckeditor/ckeditor5-image/theme/image.css */
& .image-inline picture {
  display: flex;
}
/* @ckeditor/ckeditor5-image/theme/image.css */
& .image-inline picture,
& .image-inline img {
  flex-grow: 1;
  flex-shrink: 1;
  max-width: 100%;
}
/* @ckeditor/ckeditor5-image/theme/imageresize.css */
& img.image_resized {
  height: auto;
}
/* @ckeditor/ckeditor5-image/theme/imageresize.css */
& .image.image_resized {
  max-width: 100%;
  display: block;
  box-sizing: border-box;
}
/* @ckeditor/ckeditor5-image/theme/imageresize.css */
& .image.image_resized img {
  width: 100%;
}
/* @ckeditor/ckeditor5-image/theme/imageresize.css */
& .image.image_resized > figcaption {
  display: block;
}
/* @ckeditor/ckeditor5-image/theme/imagecaption.css */
& .image > figcaption {
  display: table-caption;
  caption-side: bottom;
  word-break: break-word;
  color: var(--ck-color-image-caption-text);
  background-color: var(--ck-color-image-caption-background);
  padding: 0.6em;
  font-size: 0.75em;
  outline-offset: -1px;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-block-align-left,
& .image-style-block-align-right {
  max-width: calc(100% - var(--ck-image-style-spacing));
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-align-left,
& .image-style-align-right {
  clear: none;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-side {
  float: right;
  margin-left: var(--ck-image-style-spacing);
  max-width: 50%;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-align-left {
  float: left;
  margin-right: var(--ck-image-style-spacing);
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-align-center {
  margin-left: auto;
  margin-right: auto;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-align-right {
  float: right;
  margin-left: var(--ck-image-style-spacing);
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-block-align-right {
  margin-right: 0;
  margin-left: auto;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-style-block-align-left {
  margin-left: 0;
  margin-right: auto;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& p + .image-style-align-left,
& p + .image-style-align-right,
& p + .image-style-side {
  margin-top: 0;
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-inline.image-style-align-left,
& .image-inline.image-style-align-right {
  margin-top: var(--ck-inline-image-style-spacing);
  margin-bottom: var(--ck-inline-image-style-spacing);
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-inline.image-style-align-left {
  margin-right: var(--ck-inline-image-style-spacing);
}
/* @ckeditor/ckeditor5-image/theme/imagestyle.css */
& .image-inline.image-style-align-right {
  margin-left: var(--ck-inline-image-style-spacing);
}


/** 2. Add our own styles **/

line-height: 1.5em;

// custom blockquote
& blockquote {
  border: solid 2px #000;
  margin: 1rem 0;
  padding: 1rem;
  font-style: normal;
}
& blockquote p:first-child {
  margin-top: 0;
}
& blockquote p:last-child {
  margin-bottom: 0;
}

& figure.table {
  margin: 0;
}
& figure.image {
  margin: 0 auto;
}

// increase the font size on figures
& figure.table figcaption,
& figure.image figcaption {
  font-size: 1em;
}

& figure.table td > :first-child {
  margin-top: 0;
}

& figure.table td > :last-child {
  margin-bottom: 0;
}

// in the editor, the last child of a td is the resize handle
// so we need to go to the second-last child
& .ck-editor__editable figure.table td > :nth-last-child(2) {
  margin-bottom: 0;
}

// ensure that rendered MathJax displays inline-block
& mjx-container[jax="SVG"][display="true"] {
  margin: 0.2em 0;
  display: inline-block;
}

// less drammatic padding for lists
& ul,
& ol {
  padding-left: 1.5em;
}

// keep the cke default ordered list styles
& ol:not([type]) {
  list-style-type: decimal;
}
& ol ol:not([type]) {
  list-style-type: lower-latin;
}
& ol ol ol:not([type]) {
  list-style-type: lower-roman;
}
& ol ol ol ol:not([type]) {
  list-style-type: upper-latin;
}
& ol ol ol ol ol:not([type]) {
  list-style-type: upper-roman;
}

// ensure bullets rotate through symbols
& ul:not([type]) {
  list-style-type: disc;
}
& ul ul:not([type]) {
  list-style-type: circle;
}
& ul ul ul:not([type]) {
  list-style-type: square;
}
& ul ul ul ul:not([type]) {
  list-style-type: disc;
}
& ul ul ul ul ul:not([type]) {
  list-style-type: circle;
}
& ul ul ul ul ul ul:not([type]) {
  list-style-type: square;
}
& ul ul ul ul ul ul ul:not([type]) {
  list-style-type: disc;
}
& ul ul ul ul ul ul ul ul:not([type]) {
  list-style-type: circle;
}
& ul ul ul ul ul ul ul ul ul:not([type]) {
  list-style-type: square;
}
& ul ul ul ul ul ul ul ul ul ul:not([type]) {
  list-style-type: disc;
}

& blockquote {
  font-style: unset;
}

& dfn {
  font-style: unset;
}

${coloredTableStyles}
`;
